import { useState, useMemo } from 'react';
import Map from '@rio-cloud/rio-uikit/Map';
import MarkerLayer from '@rio-cloud/rio-uikit/MarkerLayer';
import EventUtils from '@rio-cloud/rio-uikit/EventUtils';
import MapSettings from '@rio-cloud/rio-uikit/MapSettings';
import MapTypeSettings from '@rio-cloud/rio-uikit/MapTypeSettings';
import MapClusterSettings from '@rio-cloud/rio-uikit/MapClusterSettings';
import MapAdditionalLayerSettings from '@rio-cloud/rio-uikit/MapAdditionalLayerSettings';
import MapWorkshopPoiSettingsItem from '@rio-cloud/rio-uikit/MapWorkshopPoiSettingsItem';
import SingleMapMarker from '@rio-cloud/rio-uikit/SingleMapMarker';
import ClusterMapMarker from '@rio-cloud/rio-uikit/ClusterMapMarker';
import Marker from '@rio-cloud/rio-uikit/Marker';
import type { MapEvent, Position } from '@rio-cloud/rio-uikit/mapTypes';
import { ISandboxMarker } from 'src/components/Map';
import { useMapDrawingContext } from 'src/providers/MapDrawingContext';

interface Props {
    position: Position;
    zoom: number;
    markers: ISandboxMarker[];
    showInterestPoints: boolean;
    handleSelect: (id: string) => void;
}

const MapCluster = ({ position, zoom, markers, showInterestPoints, handleSelect }: Props) => {
    const {
        interestPoints,
    } = useMapDrawingContext();

    const [showCluster, setShowCluster] = useState(true);
    const [showCustomerPois, setShowCustomerPois] = useState(true);
    const [activePoiLayers, setActivePoiLayers] = useState<string[]>([]);

    const simpleTheme = useMemo(
        () => ({
            getNoisePresentation: ({ name, numberWarnings, numberExceptions, type, iconNames }: any) => (
                <SingleMapMarker
                    iconNames={iconNames || [type]}
                    name={name}
                    warningCount={numberWarnings}
                    exceptionCount={numberExceptions}
                />
            ),
            getClusterPresentation: (assets: any[]) => (
                <ClusterMapMarker
                    count={assets.length}
                    warningCount={0}
                    exceptionCount={0}
                />
            ),
        }),
        []
    );

    const poiSimpleTheme = useMemo(
        () => ({
            getNoisePresentation: ({ name, iconNames, markerColor }: any) => (
                <SingleMapMarker
                    iconNames={iconNames ? [iconNames] : ['rioglyph rioglyph-map-marker']}
                    name={name}
                    markerColor={markerColor || 'bg-map-marker-info'}
                    anchorSize="md"
                />
            ),
            getClusterPresentation: (assets: any[]) => (
                <ClusterMapMarker
                    count={assets.length}
                    warningCount={0}
                    exceptionCount={0}
                />
            ),
        }),
        []
    );

    const handleClusterEvent = (event: MapEvent) => {
        const target = event.target.getData();
        event.stopPropagation();

        if (target.isCluster()) {
        } else {
            handleSelect(target?.getData()?.data)
        }
    };

    const handleClusterChange = (newShowCluster: boolean) => setShowCluster(newShowCluster);

    const handleToggleCustomerPois = () => {
        setShowCustomerPois(!showCustomerPois);
        setActivePoiLayers(
            activePoiLayers.includes("CUSTOMER_POIS")
                ? activePoiLayers.filter(l => l !== "CUSTOMER_POIS")
                : [...activePoiLayers, "CUSTOMER_POIS"]
        );
    };

    const eventListenerMap = EventUtils.createTapOnDblTapPreventer({
        [EventUtils.TAP]: handleClusterEvent,
        [EventUtils.DBL_TAP]: handleClusterEvent,
    });

    const filteredInterestPoints = interestPoints.filter((point) => {
        if (markers.length > 0) {
            const editingMarker = markers[0];
            const isSamePosition =
                Math.abs(point.latitude - editingMarker.position.lat) < 0.0001 &&
                Math.abs(point.longitude - editingMarker.position.lng) < 0.0001;
            return !isSamePosition;
        }
        return true;
    });

    const layers = [
        <MapWorkshopPoiSettingsItem
            key="mapCustomerPoiSettingsItem"
            isActive={showCustomerPois}
            onToggle={handleToggleCustomerPois}
            label="Workshop POIs"
        />,
    ];

    return (
        <div className='width-100pct height-100pct'>
            <Map
                credentials={{
                    apikey: process.env.REACT_APP_RIO_MAP_KEY as string,
                }}
                center={position}
                zoom={zoom}
                showCluster={showCluster}
                onShowClusterChange={handleClusterChange}
                mapSettings={
                    <MapSettings
                        options={[
                            <MapTypeSettings
                                key='mapTypeSettings'
                                tooltip='Change map type'
                                dropdownHeaderText='Map views'
                                defaultTypeLabel='Default view'
                                truckTypeLabel='Truck view'
                                terrainTypeLabel='Terrain view'
                                satelliteTypeLabel='Satellite view'
                                nightTypeLabel='Night view'
                            />,
                            <MapClusterSettings
                                key='mapClusterSettings'
                                tooltip='Enable or disable marker cluster'
                                dropdownHeaderText='Vehicle cluster'
                                clusterEnabledLabel='Enable cluster'
                                clusterDisabledLabel='Disable cluster'
                            />,
                            <MapAdditionalLayerSettings
                                key='mapLayerSettingsPoi'
                                layers={layers}
                                activeLayers={activePoiLayers}
                                tooltip='Toggle various POI layers'
                            />,
                        ]}
                    />
                }
            >
                <MarkerLayer data={markers?.map((res) => {
                    return {
                        lat: res?.position?.lat,
                        lng: res?.position?.lng,
                        data: res?.id,
                        name: res?.markerProps?.name,
                        iconNames: res?.markerProps?.iconNames,
                        type: res?.type
                    }
                })} eventListenerMap={eventListenerMap} simpleTheme={simpleTheme} />

                {showInterestPoints && showCustomerPois && filteredInterestPoints.length > 0 && (
                    showCluster ? (
                        <MarkerLayer
                            data={filteredInterestPoints.map((res) => {
                                const isConcessionaria = res.categoria?.toLowerCase().includes("concession");
                                return {
                                    lat: res.latitude,
                                    lng: res.longitude,
                                    data: res.id?.toString(),
                                    name: res.nome,
                                    iconNames: isConcessionaria
                                        ? "rioglyph rioglyph-workshop"
                                        : "rioglyph rioglyph-map-marker",
                                    markerColor: isConcessionaria ? "bg-map-marker-poi" : "bg-map-marker-info",
                                };
                            })}
                            simpleTheme={poiSimpleTheme}
                            eventListenerMap={{}}
                        />
                    ) : (
                        filteredInterestPoints.map((point, index) => {
                            const isConcessionaria = point.categoria?.toLowerCase().includes("concession");
                            return (
                                <Marker
                                    key={`interest-point-${point.id ?? index}`}
                                    position={{ lat: point.latitude, lng: point.longitude }}
                                    icon={
                                        <SingleMapMarker
                                            name={point.nome || point.endereco || "Ponto de Interesse"}
                                            markerColor={isConcessionaria ? "bg-map-marker-poi" : "bg-map-marker-info"}
                                            iconNames={
                                                isConcessionaria
                                                    ? ["rioglyph rioglyph-workshop"]
                                                    : ["rioglyph rioglyph-map-marker"]
                                            }
                                            anchorSize="md"
                                        />
                                    }
                                    draggable={false}
                                    eventListenerMap={{}}
                                />
                            );
                        })
                    )
                )}
            </Map>
        </div>
    );
};

export default MapCluster;
